"use client";

import { CircleHelp } from "lucide-react";
import Link from "next/link";
import * as React from "react";

import { pick, type Copy, type SupplyLang } from "../_lib/lang";
import { useHowto } from "./howto-store";

export const HOWTO_COPY = {
  trigger: { ms: "Macam mana guna", en: "How this works" },
  close: { ms: "Tutup panduan", en: "Close how-to" },
  dismiss: { ms: "Faham", en: "Got it" },
  fullGuide: { ms: "Panduan penuh", en: "Full guide" },
} satisfies Record<string, Copy>;

/**
 * The ONE wrapper every surface's how-to slot wears. Lives here (not in the
 * page) so a collapsed strip renders NOTHING — no wrapper, no padding, zero
 * vertical chrome. Before this, six pages hand-rolled `px-3.5 pt-2` /
 * `px-[18px] pt-2` / `px-[18px] pb-3.5` around a 44px "?" row that stayed
 * in layout even when collapsed (polish-pass audit 2026-08-23 §1).
 */
const HOWTO_SLOT = "px-3.5 pb-2 pt-2";

/**
 * A per-surface, dismissible 3-bullet how-to card.
 *
 * Contract (polish-pass PR1): the header's circled "?" (SupplyHeader with
 * `howto`, via HowToTrigger) is the ONLY trigger. This component is the
 * page-slot CARD and nothing else — collapsed it returns `null`, so it costs
 * no chrome at all; open it renders full-width in its page slot, inside the
 * page's scroll container (<main>), never pinned above it. The full user
 * guide is a link INSIDE the card, so the header button no longer competes
 * with a second "?" 50px away.
 *
 * State lives in HowtoProvider (howto-store.tsx): the page wraps header and
 * main in it, passing `initiallyDismissed` from the dismissal cookie so SSR
 * paints the real state. Mounted without a provider it renders nothing.
 */
export function HowToStrip(props: {
  lang: SupplyLang;
  bullets: Copy[];
}): React.ReactElement | null {
  const { lang, bullets } = props;
  const store = useHowto();

  if (process.env.NODE_ENV !== "production" && bullets.length > 3) {
    throw new Error(
      `HowToStrip("${store?.surface ?? "?"}") was handed ${bullets.length} bullets — max 3, the strip is meant to be skimmable, not a manual.`,
    );
  }

  if (!store || !store.open) return null;

  return (
    <div className={HOWTO_SLOT}>
      <div
        role="region"
        aria-label={pick(lang, HOWTO_COPY.trigger)}
        className="w-full rounded-[12px] border border-[var(--border)] bg-[color-mix(in_srgb,var(--info)_10%,var(--surface))] p-4"
      >
        <ol className="m-0 grid list-none gap-2.5 p-0">
          {bullets.map((bullet, index) => (
            <li key={index} className="flex items-start gap-3">
              <span
                aria-hidden
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--info-well)] text-[12px] font-black text-[var(--info)]"
              >
                {index + 1}
              </span>
              <span className="text-[13px] font-semibold leading-relaxed text-[var(--text)]">
                {pick(lang, bullet)}
              </span>
            </li>
          ))}
        </ol>
        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <button
            type="button"
            onClick={store.dismiss}
            className="flex min-h-[44px] items-center justify-center rounded-full bg-[var(--info-well)] px-4 text-[13px] font-extrabold text-[var(--info)]"
          >
            {pick(lang, HOWTO_COPY.dismiss)}
          </button>
          <Link
            href="/supply/guide"
            prefetch={false}
            className="flex min-h-[44px] items-center gap-1.5 rounded-full px-3 text-[13px] font-bold text-[var(--info)] underline-offset-2 hover:underline"
          >
            {pick(lang, HOWTO_COPY.fullGuide)}
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * The header's circled "?" when the surface carries a how-to. Same recipe as
 * the guide <Link> it replaces: a 34px visual box whose REAL hit target is
 * the `before:absolute before:-inset-[5px]` pseudo overlay (44px). That
 * overlay is what satisfies the touch-target-floor guard — the guard does
 * not read `size-[34px]` as a height at all, it accepts the element on the
 * pseudo hit-area, exactly as it does the outlet pill and avatar trigger in
 * SupplyHeader. Without a provider it degrades to the plain guide link, so a
 * surface that forgot to wrap in HowtoProvider still has a working help door.
 */
export function HowToTrigger(props: { lang: SupplyLang }): React.ReactElement {
  const { lang } = props;
  const store = useHowto();
  const className =
    "relative flex size-[34px] shrink-0 items-center justify-center rounded-[12px] border border-[var(--border)] bg-[var(--raised)] text-[var(--muted)] transition active:scale-[0.97] before:absolute before:-inset-[5px] before:content-['']";
  if (!store) {
    return (
      <Link href="/supply/guide" prefetch={false} aria-label={pick(lang, HOWTO_COPY.trigger)} className={className}>
        <CircleHelp className="size-[18px]" strokeWidth={2.3} aria-hidden />
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={store.toggle}
      aria-expanded={store.open}
      aria-label={pick(lang, store.open ? HOWTO_COPY.close : HOWTO_COPY.trigger)}
      className={`${className}${store.open ? " text-[var(--info)]" : ""}`}
    >
      <CircleHelp className="size-[18px]" strokeWidth={2.3} aria-hidden />
    </button>
  );
}
